import React, { useState, useRef, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoChevronForward, IoCalendarOutline, IoChevronDown } from "react-icons/io5";
import { format, parse, isValid } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./JobCalendar.css";
import noOverlapTouching from "./noOverlapTouching";

const localizer = momentLocalizer(moment);
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const NAV = { PREVIOUS: "PREV", NEXT: "NEXT", TODAY: "TODAY", DATE: "DATE" };

function CalendarToolbar({ label, date, view, views, localizer, onNavigate, onView }) {
  const [open, setOpen] = useState(false);
  const [pickMonth, setPickMonth] = useState(() => (date ? date.getMonth() : new Date().getMonth()));
  const [pickYear, setPickYear] = useState(() => (date ? date.getFullYear() : new Date().getFullYear()));
  const [pickDay, setPickDay] = useState(() => (date ? date.getDate() : new Date().getDate()));
  const ref = useRef(null);
  const { messages } = localizer || {};

  useEffect(() => {
    if (!date) return;
    setPickMonth(date.getMonth());
    setPickYear(date.getFullYear());
    setPickDay(date.getDate());
  }, [date?.getTime()]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const yearMin = new Date().getFullYear() - 2;
  const yearMax = new Date().getFullYear() + 2;
  const years = [];
  for (let y = yearMin; y <= yearMax; y++) years.push(y);
  const daysInMonth = new Date(pickYear, pickMonth + 1, 0).getDate();

  const handleGo = () => {
    const d = Math.min(pickDay, daysInMonth);
    onNavigate(NAV.DATE, new Date(pickYear, pickMonth, d));
    setOpen(false);
  };

  return (
    <div className="rbc-toolbar rbc-toolbar--redesigned">
      <div className="rbc-toolbar-nav-and-date" ref={ref}>
        <button type="button" className="rbc-toolbar-icon-btn" onClick={() => onNavigate(NAV.TODAY)} title={messages?.today || "Today"}>
          <IoCalendarOutline />
        </button>
        <button type="button" className="rbc-toolbar-icon-btn" onClick={() => onNavigate(NAV.PREVIOUS)} aria-label={messages?.previous || "Previous"}>
          <IoChevronBack />
        </button>
        <button type="button" className="rbc-toolbar-date-btn" onClick={() => setOpen((o) => !o)}>
          <span className="rbc-toolbar-date-text">{label}</span>
          <IoChevronDown className="rbc-toolbar-date-chevron" />
        </button>
        <button type="button" className="rbc-toolbar-icon-btn" onClick={() => onNavigate(NAV.NEXT)} aria-label={messages?.next || "Next"}>
          <IoChevronForward />
        </button>
        {open && (
          <div className="rbc-date-picker-popover">
            <div className="rbc-date-picker-title">Jump to date</div>
            {view === "day" && (
              <div className="rbc-date-picker-row">
                <label>Day</label>
                <select value={pickDay} onChange={(e) => setPickDay(Number(e.target.value))}>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="rbc-date-picker-row">
              <label>Month</label>
              <select value={pickMonth} onChange={(e) => setPickMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
            </div>
            <div className="rbc-date-picker-row">
              <label>Year</label>
              <select value={pickYear} onChange={(e) => setPickYear(Number(e.target.value))}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="rbc-date-picker-actions">
              <button type="button" onClick={handleGo}>Apply</button>
            </div>
          </div>
        )}
      </div>
      <span className="rbc-btn-group rbc-toolbar-views">
        {(views || []).map((name) => (
          <button type="button" key={name} className={view === name ? "rbc-active" : ""} onClick={() => onView(name)}>
            {(messages && messages[name]) || name}
          </button>
        ))}
      </span>
    </div>
  );
}

const STATUS_STYLES = {
  COMPLETED: { bg: "#dcfce7", border: "#166534" },
  PENDING: { bg: "#fef9c3", border: "#854d0e" },
  IN_PROGRESS: { bg: "#dbeafe", border: "#1e40af" },
  UPCOMING: { bg: "#e0e7ff", border: "#3730a3" },
  STARTED: { bg: "#dbeafe", border: "#1e40af" },
  PAST_DUE: { bg: "#fee2e2", border: "#991b1b" },
  CANCELLED: { bg: "#fee2e2", border: "#991b1b" },
  SKIPPED: { bg: "#f3f4f6", border: "#4b5563" },
  FAILED: { bg: "#fee2e2", border: "#991b1b" },
  default: { bg: "#f3f4f6", border: "#6b7280" },
};

function toDate(dateStr, timeStr) {
  if (!dateStr) return null;
  let base;
  if (typeof dateStr === "string" && dateStr.includes("T")) base = new Date(dateStr);
  else base = parse(dateStr, "yyyy-MM-dd", new Date());
  if (!isValid(base)) return null;
  if (!timeStr) return base;
  if (typeof timeStr === "string" && timeStr.match(/^\d{1,2}:\d{2}$/)) {
    const [h, m] = timeStr.split(":").map(Number);
    const d = new Date(base);
    d.setHours(h, m, 0, 0);
    return d;
  }
  const t = new Date(timeStr);
  if (!isNaN(t.getTime())) {
    const d = new Date(base);
    d.setHours(t.getHours(), t.getMinutes(), 0, 0);
    return d;
  }
  return base;
}

export function jobsToEvents(jobs) {
  if (!Array.isArray(jobs)) return [];
  return jobs
    .map((job) => {
      const dateStr = job.scheduledDate ? (typeof job.scheduledDate === "string" && job.scheduledDate.includes("T") ? format(new Date(job.scheduledDate), "yyyy-MM-dd") : job.scheduledDate) : null;
      const start = toDate(dateStr, job.scheduledStartTime);
      const end = toDate(dateStr, job.scheduledEndTime);
      if (!start) return null;
      const endDate = end && end > start ? end : new Date(start.getTime() + 30 * 60000);
      const employeeName =
        job?.assignedEmployee?.name ||
        job?.assignedEmployeeId?.name ||
        (typeof job?.assignedEmployee === "string" ? job.assignedEmployee : "") ||
        "";
      return {
        id: job._id,
        title: employeeName ? `${job.title} — ${employeeName}` : job.title,
        start,
        end: endDate,
        resource: job,
      };
    })
    .filter(Boolean);
}

export default function JobCalendar({ jobs, showArchived, date, view, onNavigate, onView, readOnly = false, onSelectEvent }) {
  const navigate = useNavigate();
  const events = jobsToEvents(jobs);

  const eventPropGetter = (event) => {
    const job = event.resource;
    const archived = showArchived && job?.isArchived;
    const style = archived ? { bg: "#fef3c7", border: "#92400e" } : (STATUS_STYLES[job?.status] || STATUS_STYLES.default);
    return { style: { backgroundColor: style.bg, borderLeft: `4px solid ${style.border}`, color: "#166473", cursor: readOnly ? "default" : "pointer" } };
  };

  return (
    <div className="job-calendar-wrap h-[calc(100vh-420px)] min-h-[400px]">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        date={date || new Date()}
        view={view || "month"}
        onNavigate={onNavigate}
        onView={onView}
        onSelectEvent={
          readOnly
            ? undefined
            : (onSelectEvent ? onSelectEvent : (e) => navigate(`/view-job/${e.id}`))
        }
        eventPropGetter={eventPropGetter}
        views={["month", "week", "day"]}
        components={{ toolbar: CalendarToolbar }}
        dayLayoutAlgorithm={noOverlapTouching}
      />
    </div>
  );
}
